import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useMemo } from 'react';
import { NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import BooleanToggle from './controls/BooleanToggle';
import DateRangeControl from './controls/DateRangeControl';
import ExistenceToggle from './controls/ExistenceToggle';
import MinMaxInputs from './controls/MinMaxInputs';
import FilterDropdown from './FilterDropdown';
import type {
  BooleanFilterValue,
  DateRangeFilterValue,
  EnumFilterValue,
  ExistenceFilterValue,
  FilterFieldDef,
  FilterRow,
  NumericRangeFilterValue,
  SmartFilterContext,
  UserReferenceFilterValue,
} from './smartFilterTypes';
import type { FilterOption } from './types';

interface SmartFilterRowProps {
  row: FilterRow;
  fieldDefs: FilterFieldDef[];
  dynamicOptions?: Record<string, FilterOption[]>;
  context: SmartFilterContext;
  onChange: (id: string, updates: Partial<FilterRow>) => void;
  onRemove: (id: string) => void;
}

const FIELD_SELECT_SX = {
  minWidth: { xs: 0, sm: 180 },
  maxWidth: { xs: '100%', sm: 200 },
  width: { xs: '100%', sm: 'auto' },
  '& .MuiOutlinedInput-root, &.MuiOutlinedInput-root': {
    height: 36,
    borderRadius: RADIUS_ITEM,
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.85rem',
  },
} as const;

const TEXT_INPUT_SX = {
  minWidth: { xs: 0, sm: 200 },
  width: { xs: '100%', sm: 'auto' },
  '& .MuiOutlinedInput-root': {
    height: 36,
    borderRadius: RADIUS_ITEM,
    backgroundColor: SURFACE_CONTAINER_LOWEST,
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.85rem',
  },
} as const;

const USER_SELECT_SX = {
  minWidth: { xs: 0, sm: 200 },
  width: { xs: '100%', sm: 'auto' },
  borderRadius: RADIUS_ITEM,
  backgroundColor: SURFACE_CONTAINER_LOWEST,
  fontFamily: 'Jost, sans-serif',
  fontSize: '0.85rem',
  height: 36,
} as const;

const SmartFilterRow: React.FC<SmartFilterRowProps> = ({
  row,
  fieldDefs,
  dynamicOptions,
  context,
  onChange,
  onRemove,
}) => {
  // Visible fields (respecting visibleWhen)
  const visibleFields = useMemo(
    () => fieldDefs.filter((f) => !f.visibleWhen || f.visibleWhen(context)),
    [fieldDefs, context]
  );

  // Group fields for the dropdown
  const groupedFields = useMemo(() => {
    const groups = new Map<string, FilterFieldDef[]>();
    for (const f of visibleFields) {
      const group = f.group ?? 'Otros';
      const existing = groups.get(group) ?? [];
      existing.push(f);
      groups.set(group, existing);
    }
    return groups;
  }, [visibleFields]);

  const currentDef = fieldDefs.find((f) => (f.id ?? f.backendField) === row.fieldId);

  const handleFieldChange = (newFieldId: string) => {
    onChange(row.id, { fieldId: newFieldId, value: null });
  };

  const handleValueChange = (value: unknown) => {
    onChange(row.id, { value });
  };

  // Render the grouped field selector
  const renderFieldSelect = () => {
    const menuItems: React.ReactNode[] = [];
    for (const [group, fields] of groupedFields) {
      menuItems.push(
        <ListSubheader
          key={`group-${group}`}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: `${NAVY}60`,
            lineHeight: '28px',
            backgroundColor: 'transparent',
          }}
        >
          {group}
        </ListSubheader>
      );
      for (const f of fields) {
        menuItems.push(
          <MenuItem
            key={f.id ?? f.backendField}
            value={f.id ?? f.backendField}
            sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', py: 0.5 }}
          >
            {f.label}
          </MenuItem>
        );
      }
    }

    return (
      <Select
        size="small"
        displayEmpty
        value={row.fieldId || ''}
        onChange={(e) => handleFieldChange(e.target.value)}
        sx={FIELD_SELECT_SX}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: `${NAVY}60`, fontFamily: 'Jost, sans-serif', fontSize: '0.85rem' }}>
                Campo
              </Typography>
            );
          }
          return currentDef?.label ?? selected;
        }}
      >
        {menuItems}
      </Select>
    );
  };

  // Render the value control based on the field's type
  const renderValueControl = () => {
    if (!currentDef) return null;

    switch (currentDef.type) {
      case 'dateRange':
        return <DateRangeControl value={row.value as DateRangeFilterValue | null} onChange={handleValueChange} />;

      case 'boolean':
        return <BooleanToggle value={row.value as BooleanFilterValue} onChange={handleValueChange} />;

      case 'enum': {
        const options =
          currentDef.options ??
          (currentDef.dynamicOptionsKey ? dynamicOptions?.[currentDef.dynamicOptionsKey] : undefined) ??
          [];
        return (
          <FilterDropdown
            options={options}
            selectedValues={(row.value as EnumFilterValue) ?? []}
            onChange={handleValueChange}
          />
        );
      }

      case 'userReference': {
        const options = currentDef.dynamicOptionsKey
          ? (dynamicOptions?.[currentDef.dynamicOptionsKey] ?? [])
          : (currentDef.options ?? []);
        return (
          <Select
            size="small"
            displayEmpty
            value={(row.value as UserReferenceFilterValue) ?? ''}
            onChange={(e) => handleValueChange(e.target.value || null)}
            sx={USER_SELECT_SX}
          >
            <MenuItem value="">
              <Typography
                sx={{ fontWeight: 400, fontSize: '0.85rem', color: 'text.secondary', fontFamily: 'Jost, sans-serif' }}
              >
                Todos
              </Typography>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'Jost, sans-serif' }}>
                  {opt.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        );
      }

      case 'textSearch':
        return (
          <TextField
            size="small"
            placeholder={`Buscar en ${currentDef.label.toLowerCase()}…`}
            value={(row.value as string) ?? ''}
            onChange={(e) => handleValueChange(e.target.value)}
            sx={TEXT_INPUT_SX}
          />
        );

      case 'numericRange':
        return <MinMaxInputs value={row.value as NumericRangeFilterValue | null} onChange={handleValueChange} />;

      case 'existence':
        return <ExistenceToggle value={row.value as ExistenceFilterValue} onChange={handleValueChange} />;

      case 'numberList': {
        const ids = (row.value as number[] | null) ?? [];
        return (
          <TextField
            size="medium"
            placeholder="1, 2, 3…"
            value={ids.join(', ')}
            onChange={(e) => {
              const parsed = e.target.value
                .split(',')
                .map((s) => Number(s.trim()))
                .filter((n) => !Number.isNaN(n) && n > 0);
              handleValueChange(parsed.length > 0 ? parsed : null);
            }}
            sx={TEXT_INPUT_SX}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 1.5 },
        py: 1,
        px: 1.5,
        borderRadius: RADIUS_ITEM,
        backgroundColor: SURFACE_CONTAINER_LOW,
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
        {renderFieldSelect()}
        <IconButton
          size="small"
          onClick={() => onRemove(row.id)}
          sx={{ color: `${NAVY}60`, '&:hover': { color: NAVY }, display: { xs: 'flex', sm: 'none' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>{renderValueControl()}</Box>
      <IconButton
        size="small"
        onClick={() => onRemove(row.id)}
        sx={{ ml: 'auto', color: `${NAVY}60`, '&:hover': { color: NAVY }, display: { xs: 'none', sm: 'flex' } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default SmartFilterRow;
